import Vue from "vue";
import Router from "vue-router";
import login from "../pages/login/Login";
import firebase from "firebase/app";
import "firebase/auth";
import "firebase/database";
import { permissions, resolveMarketingLeadsEnabled, resolveLandingPageEnabled, resolveMarketingBusinessPlanEnabled, resolveMarketingCampaignsEnabled, resolveAffiliatesMarketingEnabled, resolveAffiliatesDashboardEnabled, resolveAffiliatesFinanceEnabled, resolveAffiliatesPlansEnabled, resolveAcademyHubEnabled, resolveNpsSurveysEnabled, canAccessFinanceRoute, canAccessServersRoute } from "../utils/permissions";
import { logModuleAccessIfChanged } from "../utils/moduleAccessLog";

Vue.use(Router);

const getCurrentPosition = async () => {
  const uid = window.uid;
  if (!uid) return null;
  const snapshot = await firebase
    .database()
    .ref(`support/supportAffiliates/${uid}/position`)
    .once("value");
  return snapshot.val();
};
const getWalletModuleEnabled = async () => {
  const uid = window.uid;
  if (!uid) return false;
  const snapshot = await firebase
    .database()
    .ref(`support/supportAffiliates/${uid}/modules/wallet`)
    .once("value");
  return snapshot.val() === true;
};

const getAffiliateModuleEnabled = async (moduleKey) => {
  const uid = window.uid;
  if (!uid) return false;
  const snapshot = await firebase
    .database()
    .ref(`support/supportAffiliates/${uid}/modules/${moduleKey}`)
    .once("value");
  return snapshot.val() === true;
};

const getAffiliateModules = async () => {
  const uid = window.uid;
  if (!uid) return {};
  const snapshot = await firebase
    .database()
    .ref(`support/supportAffiliates/${uid}/modules`)
    .once("value");
  return snapshot.val() || {};
};

const MARKETING_ROUTES = ["marketing-leads", "marketing-landing-form", "marketing-campaigns", "marketing-business-plan"];
const NPS_ROUTES = ["nps-surveys", "new-nps-survey", "edit-nps-survey", "nps-responses"];
const ACADEMY_ROUTES = ["academy-hub", "academy-hub-uso", "academy-hub-content", "academy-hub-lesson-new", "academy-hub-lesson-edit", "academy-hub-feedbacks"];

// Cria um guard de rota a partir de um predicado de permissão (utils/permissions).
const guard = (canAccess) => async (to, from, next) => {
  try {
    if (!window.uid) {
      next({ name: "login" });
      return;
    }
    if (MARKETING_ROUTES.includes(to.name)) {
      const [position, modules] = await Promise.all([
        getCurrentPosition(),
        getAffiliateModules(),
      ]);
      const marketingAccess = {
        "marketing-leads": permissions.marketingLeads(position, resolveMarketingLeadsEnabled(modules)),
        "marketing-landing-form": permissions.marketingLandingPage(position, resolveLandingPageEnabled(modules)),
        "marketing-business-plan": permissions.marketingBusinessPlan(position, resolveMarketingBusinessPlanEnabled(modules)),
        "marketing-campaigns": permissions.marketingCampaigns(position, resolveMarketingCampaignsEnabled(modules)),
      };
      if (marketingAccess[to.name]) {
        next();
      } else {
        Vue.$toast.error("Acesso restrito.");
        next({ name: "users" });
      }
      return;
    }
    if (to.name === "affiliates-marketing") {
      const [position, modules] = await Promise.all([
        getCurrentPosition(),
        getAffiliateModules(),
      ]);
      const marketingEnabled = resolveAffiliatesMarketingEnabled(modules);
      if (permissions.affiliatesMarketing(position, marketingEnabled)) {
        next();
      } else {
        Vue.$toast.error("Acesso restrito.");
        next({ name: "users" });
      }
      return;
    }
    if (to.name === "commission-plans") {
      const [position, modules] = await Promise.all([
        getCurrentPosition(),
        getAffiliateModules(),
      ]);
      const plansEnabled = resolveAffiliatesPlansEnabled(modules);
      if (permissions.affiliatesPlans(position, plansEnabled)) {
        next();
      } else {
        Vue.$toast.error("Acesso restrito.");
        next({ name: "users" });
      }
      return;
    }
    if (to.name === "affiliates-dashboard") {
      const [position, modules] = await Promise.all([
        getCurrentPosition(),
        getAffiliateModules(),
      ]);
      const dashboardEnabled = resolveAffiliatesDashboardEnabled(modules);
      if (permissions.affiliatesDashboard(position, dashboardEnabled)) {
        next();
      } else {
        Vue.$toast.error("Acesso restrito.");
        next({ name: "users" });
      }
      return;
    }
    if (to.name === "affiliates") {
      const [position, modules] = await Promise.all([
        getCurrentPosition(),
        getAffiliateModules(),
      ]);
      const financeEnabled = resolveAffiliatesFinanceEnabled(modules);
      if (permissions.affiliatesFinance(position, financeEnabled)) {
        next();
      } else {
        Vue.$toast.error("Acesso restrito.");
        next({ name: "users" });
      }
      return;
    }
    if (to.name === "finance") {
      const [position, modules] = await Promise.all([
        getCurrentPosition(),
        getAffiliateModules(),
      ]);
      if (canAccessFinanceRoute(position, modules)) {
        next();
      } else {
        Vue.$toast.error("Acesso restrito.");
        next({ name: "users" });
      }
      return;
    }
    if (NPS_ROUTES.includes(to.name)) {
      const [position, modules] = await Promise.all([
        getCurrentPosition(),
        getAffiliateModules(),
      ]);
      if (permissions.npsSurveys(position, resolveNpsSurveysEnabled(modules))) {
        next();
      } else {
        Vue.$toast.error("Acesso restrito.");
        next({ name: "users" });
      }
      return;
    }
    if (to.name === "aplicativos") {
      const [position, modules] = await Promise.all([
        getCurrentPosition(),
        getAffiliateModules(),
      ]);
      if (permissions.aplicativos(position, modules.aplicativos === true)) {
        next();
      } else {
        Vue.$toast.error("Acesso restrito.");
        next({ name: "users" });
      }
      return;
    }
    if (to.name === "servers") {
      const [position, modules] = await Promise.all([
        getCurrentPosition(),
        getAffiliateModules(),
      ]);
      if (canAccessServersRoute(position, modules)) {
        next();
      } else {
        Vue.$toast.error("Acesso restrito.");
        next({ name: "users" });
      }
      return;
    }
    if (ACADEMY_ROUTES.includes(to.name)) {
      const [position, modules] = await Promise.all([
        getCurrentPosition(),
        getAffiliateModules(),
      ]);
      if (permissions.academyHub(position, resolveAcademyHubEnabled(modules))) {
        next();
      } else {
        Vue.$toast.error("Acesso restrito.");
        next({ name: "users" });
      }
      return;
    }
    if (to.name === "carteiras") {
      const [position, walletEnabled] = await Promise.all([
        getCurrentPosition(),
        getWalletModuleEnabled(),
      ]);
      if (permissions.carteiras(position, walletEnabled)) {
        next();
      } else {
        Vue.$toast.error("Acesso restrito.");
        next({ name: "users" });
      }
      return;
    }
    if (to.name === "suporte-dashboard") {
      const [position, supportDashboardEnabled] = await Promise.all([
        getCurrentPosition(),
        getAffiliateModuleEnabled("supportDashboard"),
      ]);
      if (permissions.suporteDashboard(position, supportDashboardEnabled)) {
        next();
      } else {
        Vue.$toast.error("Acesso restrito.");
        next({ name: "users" });
      }
      return;
    }
    if (to.name === "chamados") {
      const [position, bugReportEnabled] = await Promise.all([
        getCurrentPosition(),
        getAffiliateModuleEnabled("bugReport"),
      ]);
      if (permissions.chamados(position, bugReportEnabled)) {
        next();
      } else {
        Vue.$toast.error("Acesso restrito.");
        next({ name: "users" });
      }
      return;
    }
    if (to.name === "cancelamentos") {
      const [position, cancelamentosEnabled] = await Promise.all([
        getCurrentPosition(),
        getAffiliateModuleEnabled("cancelamentos"),
      ]);
      if (permissions.cancelamentos(position, cancelamentosEnabled)) {
        next();
      } else {
        Vue.$toast.error("Acesso restrito.");
        next({ name: "users" });
      }
      return;
    }
    if (to.name === "coupons") {
      const [position, couponsEnabled] = await Promise.all([
        getCurrentPosition(),
        getAffiliateModuleEnabled("coupons"),
      ]);
      if (permissions.coupons(position, couponsEnabled)) {
        next();
      } else {
        Vue.$toast.error("Acesso restrito.");
        next({ name: "users" });
      }
      return;
    }
    if (to.name === "commercial-leads") {
      const [position, commercialEnabled] = await Promise.all([
        getCurrentPosition(),
        getAffiliateModuleEnabled("commercial"),
      ]);
      if (permissions.commercial(position, commercialEnabled)) {
        next();
      } else {
        Vue.$toast.error("Acesso restrito.");
        next({ name: "users" });
      }
      return;
    }
    if (to.name === "master-autonomous-upsells") {
      const [position, upsellsEnabled] = await Promise.all([
        getCurrentPosition(),
        getAffiliateModuleEnabled("autonomousUpsells"),
      ]);
      if (permissions.autonomousUpsells(position, upsellsEnabled)) {
        next();
      } else {
        Vue.$toast.error("Acesso restrito.");
        next({ name: "users" });
      }
      return;
    }
    if (to.name === "master-autonomous-self-service") {
      const [position, selfServiceEnabled] = await Promise.all([
        getCurrentPosition(),
        getAffiliateModuleEnabled("autonomousSelfService"),
      ]);
      if (permissions.autonomousSelfService(position, selfServiceEnabled)) {
        next();
      } else {
        Vue.$toast.error("Acesso restrito.");
        next({ name: "users" });
      }
      return;
    }
    const position = await getCurrentPosition();
    if (canAccess(position)) {
      next();
    } else {
      Vue.$toast.error("Acesso restrito.");
      next({ name: "users" });
    }
  } catch (error) {
    console.error("Erro ao verificar permissão:", error);
    next({ name: "users" });
  }
};

const routes = [
  {
    path: "/",
    name: "index",
    meta: {
      title: "Entrar",
      public: true,
    },
    component: login,
  },
  {
    path: "/login/",
    name: "login",
    meta: {
      title: "Entrar",
      public: true,
    },
    component: login,
  },
  {
    path: "/reset/password",
    name: "reset-password",
    meta: {
      title: "Recuperar Senha",
      public: true,
    },
    component: () => import(/* webpackChunkName: "Login" */ "../pages/login/Password"),
  },
  {
    path: "/monitor/:searchValue?",
    name: "monitor",
    meta: {
      title: "Monitoramento",
    },
    component: () => import(/* webpackChunkName: "Login" */ "../pages/monitor/Monitoring"),
  },
  {
    path: "/management/",
    name: "management",
    meta: {
      title: "Gestão",
    },
    component: () => import(/* webpackChunkName: "Login" */ "../pages/management/Management"),
  },
  {
    path: "/evaluation/:uid?/:evaluationUid?",
    name: "evaluation",
    meta: {
      title: "Avaliação da Ligação",
      public: true,
    },
    component: () => import(/* webpackChunkName: "Login" */ "../pages/monitor/CallEvaluation"),
  },
  {
    path: "/users/:searchValue?",
    name: "users",
    meta: {
      title: "Clientes",
    },
    component: () => import(/* webpackChunkName: "Login" */ "../pages/users/Users"),
  },
  {
    path: "/cancelamentos/",
    name: "cancelamentos",
    meta: {
      title: "Cancelamentos",
    },
    component: () =>
      import(
        /* webpackChunkName: "Cancelamentos" */ "../pages/self-service-cancelations/SelfServiceCancelationsView"
      ),
    beforeEnter: guard(permissions.cancelamentos),
  },
  {
    path: "/auto-cancelamentos/",
    redirect: { name: "cancelamentos" },
  },
  {
    path: "/relacionamento/carteiras/",
    name: "carteiras",
    meta: {
      title: "Carteiras",
    },
    component: () => import(/* webpackChunkName: "Relacionamento" */ "../pages/relacionamento/CarteirasView"),
    beforeEnter: guard(permissions.carteiras),
  },
  {
    path: "/questions/",
    name: "questions",
    meta: {
      title: "Perguntas Frequentes",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/questions/Central"),
  },
  {
    path: "/trainings/",
    name: "trainings",
    meta: {
      title: "Treinamentos",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/trainings/Trainings"),
  },
  {
    path: "/reservas/",
    name: "reservas",
    meta: {
      title: "Reservas",
    },
    component: () => import(/* webpackChunkName: "Reservas" */ "../pages/reservas/ReservasView"),
    beforeEnter: guard(permissions.reservas),
  },
  {
    path: "/proximos-eventos/",
    name: "proximos-eventos",
    meta: {
      title: "Próximos eventos",
    },
    component: () => import(/* webpackChunkName: "ProximosEventos" */ "../pages/proximos-eventos/ProximosEventosView"),
  },
  {
    path: "/finance/",
    name: "finance",
    meta: {
      title: "Financeiro",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/finance/Finance"),
    beforeEnter: guard(permissions.finance),
  },
  {
    path: "/affiliates/dashboard",
    name: "affiliates-dashboard",
    meta: {
      title: "MoneyHub - Dashboard",
    },
    component: () =>
      import(/* webpackChunkName: "Central" */ "../pages/affiliates/AffiliatesDashboard"),
    beforeEnter: guard(permissions.affiliatesDashboard),
  },
  {
    path: "/affiliates/",
    name: "affiliates",
    meta: {
      title: "MoneyHub - Afiliados",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/affiliates/Affiliates"),
    beforeEnter: guard(permissions.affiliatesFinance),
  },
  {
    path: "/affiliates/plans",
    name: "commission-plans",
    meta: {
      title: "MoneyHub - Planos",
    },
    component: () =>
      import(/* webpackChunkName: "Central" */ "../pages/affiliates/CommissionPlans"),
    beforeEnter: guard(permissions.affiliatesPlans),
  },
  {
    path: "/affiliates/marketing",
    name: "affiliates-marketing",
    meta: {
      title: "MoneyHub - Anúncios",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/affiliates/Marketing"),
    beforeEnter: guard(permissions.affiliatesMarketing),
  },
  {
    path: "/affiliates/marketing/landing-pages",
    redirect: { name: "marketing-leads" },
  },
  {
    path: "/marketing/leads",
    name: "marketing-leads",
    meta: {
      title: "Marketing - Leads",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/leads/Leads"),
    beforeEnter: guard(permissions.marketingLeads),
  },
  {
    path: "/marketing/business-plan",
    name: "marketing-business-plan",
    meta: {
      title: "Marketing - Business Plan",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/marketing/BusinessPlan"),
    beforeEnter: guard(permissions.marketingBusinessPlan),
  },
  {
    path: "/marketing/formulario",
    name: "marketing-landing-form",
    meta: {
      title: "Marketing - Landing page",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/leads/LandingFormAnalytics"),
    beforeEnter: guard(permissions.marketingLeads),
  },
  {
    path: "/marketing/campanhas",
    name: "marketing-campaigns",
    meta: {
      title: "Marketing - Modais internos",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/leads/Campaigns"),
    beforeEnter: guard(permissions.marketingCampaigns),
  },
  {
    path: "/updates/",
    name: "updates",
    meta: {
      title: "Atualizações",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/update/Updates"),
  },
  {
    path: "/aplicativos/",
    name: "aplicativos",
    meta: {
      title: "Aplicativos",
    },
    component: () => import(/* webpackChunkName: "Aplicativos" */ "../pages/aplicativos/AplicativosView"),
    beforeEnter: guard(permissions.aplicativos),
  },
  {
    path: "/updates/analytics/",
    name: "updates-analytics",
    meta: {
      title: "Visão analítica - Atualizações",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/update/UpdatesAnalytics"),
    beforeEnter: guard(permissions.updates),
  },
  {
    path: "/updates/new/",
    name: "new-update",
    meta: {
      title: "Nova Atualização",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/update/NewUpdate"),
    beforeEnter: guard(permissions.updates),
  },
  {
    path: "/updates/edit/:id",
    name: "edit-update",
    meta: {
      title: "Editar Atualização",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/update/NewUpdate"),
    beforeEnter: guard(permissions.updates),
  },
  {
    path: "/nps-surveys/",
    name: "nps-surveys",
    meta: {
      title: "Pesquisas NPS",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/nps-survey/NPSSurveys"),
    beforeEnter: guard(permissions.npsSurveys),
  },
  {
    path: "/nps-surveys/new/",
    name: "new-nps-survey",
    meta: {
      title: "Nova Pesquisa NPS",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/nps-survey/NewNPSSurvey"),
    beforeEnter: guard(permissions.npsSurveys),
  },
  {
    path: "/nps-surveys/edit/:id",
    name: "edit-nps-survey",
    meta: {
      title: "Editar Pesquisa NPS",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/nps-survey/NewNPSSurvey"),
    beforeEnter: guard(permissions.npsSurveys),
  },
  {
    path: "/nps-surveys/responses/:id",
    name: "nps-responses",
    meta: {
      title: "Respostas NPS",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/nps-survey/NPSResponses"),
    beforeEnter: guard(permissions.npsSurveys),
  },
  {
    path: "/academy-hub/",
    name: "academy-hub",
    meta: {
      title: "AcademyHub",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/hub-academy/AcademyHubDashboard"),
    beforeEnter: guard(permissions.academyHub),
  },
  {
    path: "/academy-hub/uso/",
    name: "academy-hub-uso",
    meta: {
      title: "AcademyHub | Usuários",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/hub-academy/AcademyHubAnalytics"),
    beforeEnter: guard(permissions.academyHub),
  },
  {
    path: "/academy-hub/content/",
    name: "academy-hub-content",
    meta: {
      title: "AcademyHub | Conteúdo",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/hub-academy/AcademyHubContent"),
    beforeEnter: guard(permissions.academyHub),
  },
  {
    path: "/academy-hub/content/lesson/new/",
    name: "academy-hub-lesson-new",
    meta: {
      title: "AcademyHub | Nova aula",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/hub-academy/AcademyHubLessonForm"),
    beforeEnter: guard(permissions.academyHub),
  },
  {
    path: "/academy-hub/content/:courseId/:lessonId/",
    name: "academy-hub-lesson-edit",
    meta: {
      title: "AcademyHub | Editar aula",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/hub-academy/AcademyHubLessonForm"),
    beforeEnter: guard(permissions.academyHub),
  },
  {
    path: "/academy-hub/feedbacks/",
    name: "academy-hub-feedbacks",
    meta: {
      title: "AcademyHub | Feedbacks",
    },
    component: () => import(/* webpackChunkName: "Central" */ "../pages/hub-academy/AcademyHubFeedbacks"),
    beforeEnter: guard(permissions.academyHub),
  },
  // {
  //   path: '/settings/',
  //   name: 'settings',
  //   meta: {
  //     title: 'Configurações'
  //   },
  //   component: () => import(/* webpackChunkName: "Settings" */ '../pages/settings/Settings')
  // },
  {
    path: "/servers/",
    name: "servers",
    meta: {
      title: "Monitor de Servidores",
      requiresMaster: true,
    },
    component: () => import(/* webpackChunkName: "Servers" */ "../pages/servers/ServerMonitor"),
    beforeEnter: guard(canAccessServersRoute),
  },
  {
    path: "/comercial/leads/",
    name: "commercial-leads",
    meta: {
      title: "Comercial",
    },
    component: () => import(/* webpackChunkName: "Comercial" */ "../pages/commercial/CommercialLeads.vue"),
    beforeEnter: guard(permissions.commercial),
  },
  {
    path: "/cupons/",
    name: "coupons",
    meta: {
      title: "Cupons",
    },
    component: () => import(/* webpackChunkName: "Cupons" */ "../pages/coupons/Coupons.vue"),
    beforeEnter: guard(permissions.coupons),
  },
  {
    path: "/master/logs/",
    name: "master-logs",
    meta: {
      title: "Histórico",
    },
    component: () => import(/* webpackChunkName: "Master" */ "../pages/master/Logs"),
    beforeEnter: guard(permissions.master),
  },
  {
    path: "/master/autonomos/upsells/",
    name: "master-autonomous-upsells",
    meta: {
      title: "Autônomos | Upsells",
    },
    component: () => import(/* webpackChunkName: "Master" */ "../pages/master/autonomous/MasterAutonomousUpsells.vue"),
    beforeEnter: guard(permissions.autonomousUpsells),
  },
  {
    path: "/master/autonomos/self-service/",
    name: "master-autonomous-self-service",
    meta: {
      title: "Autônomos | Self-service",
    },
    component: () => import(/* webpackChunkName: "Master" */ "../pages/master/autonomous/MasterSelfServiceAnalytics.vue"),
    beforeEnter: guard(permissions.autonomousSelfService),
  },
  {
    path: "/master/users/",
    name: "master-users",
    meta: {
      title: "Usuários",
    },
    component: () => import(/* webpackChunkName: "Master" */ "../pages/master/Users"),
    beforeEnter: guard(permissions.master),
  },
  {
    path: "/suporte/chamados/",
    name: "chamados",
    meta: {
      title: "Chamados",
    },
    component: () => import(/* webpackChunkName: "Suporte" */ "../pages/suporte/ChamadosView"),
    beforeEnter: guard(permissions.chamados),
  },
  {
    path: "/suporte/dashboard/",
    name: "suporte-dashboard",
    meta: {
      title: "Dashboard Suporte",
    },
    component: () => import(/* webpackChunkName: "Suporte" */ "../pages/suporte/SuporteDashboard"),
    beforeEnter: guard(permissions.suporteDashboard),
  },
  {
    path: "*",
    name: "not-found",
    meta: {
      title: "Página não encontrada",
    },
    component: () => import(/* webpackChunkName: "NotFound" */ "../pages/not-found/NotFound"),
  },
];

const router = new Router({
  mode: "history",
  routes,
});

router.beforeEach((to, from, next) => {
  document.title = "HUBSTROM CRM | " + to.meta.title;
  document.public = to.meta.public;
  if (to.matched.some((record) => !record.meta.public)) {
    if (!firebase.auth().currentUser) {
      next({ name: "login" });
    } else {
      next();
    }
  } else {
    next();
  }

  // if (!window.uid && !to.meta.public) {
  //   next({ name: 'login' })
  // } else {
  //   next()
  // }
});

router.afterEach((to, from) => {
  logModuleAccessIfChanged(firebase, to, from);
});

export default router;
